import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { DEFAULT_MAILBOX } from "../../utils/types/email";
import {
  fetchEmails,
  fetchEmailById,
  toggleEmailFlag,
} from "../thunks/emailThunks";

import type { UIMessage } from "../../hooks/useAiAssistant";
import type { EmailListItem, EmailDetails, MashedEmail, MailboxKey, EmailViewMode } from "../../utils/types/email";

export { DEFAULT_MAILBOX } from "../../utils/types/email";

export interface AiReviewData {
  content: string;
  suggestions: string[];
  hasGenerated: boolean;
  timestamp: number;
}

export interface AiChatData {
  messages: UIMessage[];
  lastUpdated: number;
}

export type SortOrder = 'receivedDateTime desc' | 'receivedDateTime asc' | 'from/emailAddress/name asc' | 'from/emailAddress/name desc';

export interface OpenEmailTab {
  id: string;
  emailId: string;
  mailbox: MailboxKey;
  title: string;
  mashedEmail: MashedEmail | null;
  pinned: boolean;
}

interface EmailState {
  emails: EmailListItem[];
  selectedEmailId: string | null;
  selectedEmailDetails: EmailDetails | null;
  currentMashedEmail:MashedEmail|null;
  openEmailTabs: OpenEmailTab[];
  activeEmailTabId: string | null;
  previewEmailTabId: string | null;
  emailDetailsById: Record<string, EmailDetails>;
  detailsLoadingById: Record<string, boolean>;
  currentFolder: string;
  currentMailbox: MailboxKey;
  listLoading: boolean;
  detailsLoading: boolean;
  error: string | null;
  unreadCount: number;
  aiReviews: Record<string, AiReviewData>;
  aiChats: Record<string, AiChatData>;
  drafts: Record<string, string>;
  draftSubjects: Record<string, string>;
  // Persistent UI state — survives tab/screen navigation
  sortOrder: SortOrder;
  activeFilter: string | null;
  myVoyagesEnabled: boolean;
  viewMode: EmailViewMode;
}

const initialState: EmailState = {
  emails: [],
  selectedEmailId: null,
  selectedEmailDetails: null,
  currentMashedEmail:null,
  openEmailTabs: [],
  activeEmailTabId: null,
  previewEmailTabId: null,
  emailDetailsById: {},
  detailsLoadingById: {},
  currentFolder: "", // Empty means "use backend default Inbox" until a real folder is chosen
  currentMailbox: DEFAULT_MAILBOX,
  listLoading: false,
  detailsLoading: false,
  error: null,
  unreadCount: 0,
  aiReviews: {},
  aiChats: {},
  drafts: {},
  draftSubjects: {},
  sortOrder: 'receivedDateTime desc',
  activeFilter: null,
  myVoyagesEnabled: false,
  viewMode: "flat",
};

const getEmailTabId = (mailbox: MailboxKey, emailId: string) =>
  `${mailbox}:${emailId}`;

const getEmailIdFromFetchArg = (
  arg: string | { id: string; mailbox?: MailboxKey },
) => (typeof arg === "string" ? arg : arg.id);

const syncSelectedEmailFromActiveTab = (state: EmailState) => {
  const activeTab = state.openEmailTabs.find(
    (tab) => tab.id === state.activeEmailTabId,
  );

  state.selectedEmailId = activeTab?.emailId ?? null;
  state.currentMashedEmail = activeTab?.mashedEmail ?? null;
  state.selectedEmailDetails = activeTab
    ? state.emailDetailsById[activeTab.emailId] ?? null
    : null;
};

const emailSlice = createSlice({
  name: "emails",
  initialState,
  reducers: {
    setViewMode: (state, action: PayloadAction<EmailViewMode>) => {
      state.viewMode = action.payload;
    },
    selectEmail: (state, action: PayloadAction<{
      emailId: string | null;
      mashedEmail:MashedEmail|null;
    }|null>) => {
      state.selectedEmailId = action.payload?.emailId||null;
      state.currentMashedEmail=action.payload?.mashedEmail||null;
      if (!action.payload) {
        state.selectedEmailDetails = null;
        state.activeEmailTabId = null;
      }
    },
    openEmailPreviewTab: (
      state,
      action: PayloadAction<{
        emailId: string;
        mailbox: MailboxKey;
        title: string;
        mashedEmail: MashedEmail | null;
      }>,
    ) => {
      const { emailId, mailbox, title, mashedEmail } = action.payload;
      const tabId = getEmailTabId(mailbox, emailId);
      const existingTab = state.openEmailTabs.find((tab) => tab.id === tabId);

      if (existingTab) {
        existingTab.title = title;
        existingTab.mashedEmail = mashedEmail;
        state.activeEmailTabId = existingTab.id;
        syncSelectedEmailFromActiveTab(state);
        return;
      }

      const previewIndex = state.previewEmailTabId
        ? state.openEmailTabs.findIndex(
            (tab) => tab.id === state.previewEmailTabId && !tab.pinned,
          )
        : -1;
      const previewTab: OpenEmailTab = {
        id: tabId,
        emailId,
        mailbox,
        title,
        mashedEmail,
        pinned: false,
      };

      if (previewIndex >= 0) {
        state.openEmailTabs[previewIndex] = previewTab;
      } else {
        state.openEmailTabs.push(previewTab);
      }

      state.previewEmailTabId = tabId;
      state.activeEmailTabId = tabId;
      syncSelectedEmailFromActiveTab(state);
    },
    setActiveEmailTab: (state, action: PayloadAction<string>) => {
      if (!state.openEmailTabs.some((tab) => tab.id === action.payload)) return;
      state.activeEmailTabId = action.payload;
      syncSelectedEmailFromActiveTab(state);
    },
    pinEmailTab: (state, action: PayloadAction<string>) => {
      const tab = state.openEmailTabs.find((item) => item.id === action.payload);
      if (!tab) return;

      tab.pinned = true;
      if (state.previewEmailTabId === tab.id) {
        state.previewEmailTabId = null;
      }
    },
    closeEmailTab: (state, action: PayloadAction<string>) => {
      const closingIndex = state.openEmailTabs.findIndex(
        (tab) => tab.id === action.payload,
      );
      if (closingIndex === -1) return;

      const wasActive = state.activeEmailTabId === action.payload;
      const wasPreview = state.previewEmailTabId === action.payload;
      state.openEmailTabs.splice(closingIndex, 1);
      if (wasPreview) {
        state.previewEmailTabId = null;
      }

      if (wasActive) {
        const nextIndex = Math.min(closingIndex, state.openEmailTabs.length - 1);
        state.activeEmailTabId =
          nextIndex >= 0 ? state.openEmailTabs[nextIndex].id : null;
      }

      syncSelectedEmailFromActiveTab(state);
    },
    updateOpenEmailTabMashedEmail: (
      state,
      action: PayloadAction<{
        emailId: string;
        mailbox?: MailboxKey;
        mashedEmail: MashedEmail;
      }>,
    ) => {
      const tab = state.openEmailTabs.find(
        (item) =>
          item.emailId === action.payload.emailId &&
          (!action.payload.mailbox || item.mailbox === action.payload.mailbox),
      );
      if (!tab) return;

      tab.mashedEmail = action.payload.mashedEmail;
      tab.title = action.payload.mashedEmail.email.subject || tab.title;
      if (state.activeEmailTabId === tab.id) {
        syncSelectedEmailFromActiveTab(state);
      }
    },
    changeFolder: (state, action: PayloadAction<string>) => {
      state.currentFolder = action.payload;
    },

    changeMailbox: (state, action: PayloadAction<MailboxKey>) => {
      if (state.currentMailbox === action.payload) return;
      state.currentMailbox = action.payload;
      // Reset all per-mailbox state
      state.currentFolder = "";
      state.selectedEmailId = null;
      state.selectedEmailDetails = null;
      state.currentMashedEmail = null;
      state.openEmailTabs = [];
      state.activeEmailTabId = null;
      state.previewEmailTabId = null;
      state.emails = [];
      state.activeFilter = null;
      state.myVoyagesEnabled = false;
    },

    resetSelectedEmail: (state) => {  
      state.selectedEmailId = null;
      state.selectedEmailDetails = null;
      state.currentMashedEmail=null;
      state.activeEmailTabId = null;
    },

    setAiReview: (
      state,
      action: PayloadAction<{
        emailId: string;
        content: string;
        suggestions: string[];
      }>
    ) => {
      const { emailId, content, suggestions } = action.payload;
      state.aiReviews[emailId] = {
        content,
        suggestions,
        hasGenerated: true,
        timestamp: Date.now(),
      };
    },
    addMessageToChat: (
      state,
      action: PayloadAction<{ emailId: string; message: UIMessage }>
    ) => {
      const { emailId, message } = action.payload;
      if (!state.aiChats[emailId]) {
        state.aiChats[emailId] = { messages: [], lastUpdated: Date.now() };
      }
      state.aiChats[emailId].messages.push(message);
      state.aiChats[emailId].lastUpdated = Date.now();
    },

    setAiChatHistory: (
      state,
      action: PayloadAction<{ emailId: string; messages: UIMessage[] }>
    ) => {
      const { emailId, messages } = action.payload;
      state.aiChats[emailId] = {
        messages,
        lastUpdated: Date.now(),
      };
    },

    clearAiData: (state) => {
      state.aiReviews = {};
      state.aiChats = {};
    },
    setEmailDraft: (
      state,
      action: PayloadAction<{ emailId: string; content?: string; subject?: string | null }>
    ) => {
      const { emailId, content, subject } = action.payload;
      if (content !== undefined) {
        if (content.trim()) {
          state.drafts[emailId] = content;
        } else {
          delete state.drafts[emailId];
        }
      }
      if (subject !== undefined) {
        if (subject === null) {
          delete state.draftSubjects[emailId];
        } else {
          state.draftSubjects[emailId] = subject;
        }
      }

      // Check if both are empty/cleared
      const hasBody = !!state.drafts[emailId]?.trim();
      const hasSubject = state.draftSubjects[emailId] !== undefined && state.draftSubjects[emailId] !== "";

      if (!hasBody && !hasSubject) {
        delete state.drafts[emailId];
        delete state.draftSubjects[emailId];
      }
    },

    clearEmailDraft: (state, action: PayloadAction<string>) => {
      delete state.drafts[action.payload];
      delete state.draftSubjects[action.payload];
    },

    setSortOrder: (state, action: PayloadAction<SortOrder>) => {
      state.sortOrder = action.payload;
    },

    setEmailActiveFilter: (state, action: PayloadAction<string | null>) => {
      state.activeFilter = action.payload;
    },

    setMyVoyagesEnabled: (state, action: PayloadAction<boolean>) => {
      state.myVoyagesEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmails.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(
        fetchEmails.fulfilled,
        (state, action: PayloadAction<EmailListItem[]>) => {
          state.listLoading = false;
          state.emails = action.payload;
          state.unreadCount = action.payload.filter(
            (email) => !email.isRead
          ).length;
        }
      )
      .addCase(fetchEmails.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchEmailById.pending, (state, action) => {
        const emailId = getEmailIdFromFetchArg(action.meta.arg);
        state.detailsLoading = true;
        state.detailsLoadingById[emailId] = true;
        if (state.selectedEmailId === emailId) {
          state.selectedEmailDetails = state.emailDetailsById[emailId] ?? null;
        }
        state.error = null;
      })
      .addCase(
        fetchEmailById.fulfilled,
        (state, action: PayloadAction<EmailDetails>) => {
          state.detailsLoading = false;
          const details = action.payload;
          if (details.attachments) {
            details.attachments = details.attachments.filter(
              (a) => !a.name?.toLowerCase().endsWith(".p7m")
            );
          }
          state.emailDetailsById[details.id] = details;
          state.detailsLoadingById[details.id] = false;
          if (state.selectedEmailId === details.id) {
            state.selectedEmailDetails = details;
          }
        }
      )
      .addCase(fetchEmailById.rejected, (state, action) => {
        const emailId = getEmailIdFromFetchArg(action.meta.arg);
        state.detailsLoading = false;
        state.detailsLoadingById[emailId] = false;
        state.error = action.payload as string;
      })
      .addCase(
        toggleEmailFlag.fulfilled,
        (state, action: PayloadAction<EmailDetails>) => {
          if (
            state.selectedEmailDetails &&
            state.selectedEmailDetails.id === action.payload.id
          ) {
            state.selectedEmailDetails = action.payload;
          }
          state.emailDetailsById[action.payload.id] = action.payload;
        }
      );
  },
});

export const {
  setViewMode,
  selectEmail,
  openEmailPreviewTab,
  setActiveEmailTab,
  pinEmailTab,
  closeEmailTab,
  updateOpenEmailTabMashedEmail,
  changeFolder,
  changeMailbox,
  setAiReview,
  addMessageToChat,
  setAiChatHistory,
  clearAiData,
  setEmailDraft,
  clearEmailDraft,
  resetSelectedEmail,
  setSortOrder,
  setEmailActiveFilter,
  setMyVoyagesEnabled,
} = emailSlice.actions;
export default emailSlice.reducer;
